import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { AdminUserDetailsCard } from '@/components/admin/AdminUserDetailsCard';

import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { fetchUsers } from '@/features/users';
import {
  selectAllUsers,
  selectListUsersError,
  selectListUsersHasMore,
  selectListUsersLoading,
  selectHasListedUsers,
  selectListUsersNextToken,
} from '@/features/users/usersSlice';

function normalize(s: string) {
  return s.trim().toLowerCase();
}

export default function AdminUsersPage() {
  const dispatch = useAppDispatch();
  const [searchParams, setSearchParams] = useSearchParams();
  const requestedUserId = searchParams.get('userId')?.trim() ?? '';

  const users = useAppSelector(selectAllUsers);
  const listing = useAppSelector(selectListUsersLoading);
  const hasMore = useAppSelector(selectListUsersHasMore);
  const nextToken = useAppSelector(selectListUsersNextToken);
  const hasListed = useAppSelector(selectHasListedUsers);
  const listError = useAppSelector(selectListUsersError);
  const [query, setQuery] = useState('');
  const [selectedId, setSelectedId] = useState<string>('');

  const selectUser = useCallback(
    (userId: string) => {
      setSelectedId(userId);

      const nextParams = new URLSearchParams(searchParams);
      if (userId) {
        nextParams.set('userId', userId);
      } else {
        nextParams.delete('userId');
      }
      setSearchParams(nextParams, { replace: true });
    },
    [searchParams, setSearchParams],
  );

  // initial load
  useEffect(() => {
    if (hasListed) return;
    dispatch(fetchUsers({ limit: 50 }))
      .unwrap()
      .catch((err) => {
        toast.error(err?.message || 'Failed to load users');
      });
  }, [dispatch, hasListed]);

  useEffect(() => {
    if (!requestedUserId) return;
    if (selectedId === requestedUserId) return;
    setSelectedId(requestedUserId);
  }, [requestedUserId, selectedId]);

  // keep selectedId stable (pick first user when list arrives)
  useEffect(() => {
    if (selectedId) return;
    if (users.length > 0) selectUser(users[0].applicationUserId);
  }, [selectUser, selectedId, users]);

  const selected = useMemo(() => users.find((u) => u.applicationUserId === selectedId) ?? null, [users, selectedId]);

  const filtered = useMemo(() => {
    const q = normalize(query);
    if (!q) return users;
    return users.filter((u) => {
      const name = `${u.firstName ?? ''} ${u.lastName ?? ''}`.trim();
      return (
        normalize(u.email ?? '').includes(q) ||
        normalize(name).includes(q) ||
        normalize(u.applicationUserId).includes(q) ||
        normalize(u.organizationID ?? '').includes(q)
      );
    });
  }, [users, query]);

  const loadMore = async () => {
    if (listing) return;
    if (!hasMore) return;

    try {
      await dispatch(fetchUsers({ limit: 50, nextToken: nextToken ?? undefined })).unwrap();
    } catch (err) {
      const e = err as { message?: string };
      toast.error(e?.message || 'Failed to load more users');
    }
  };

  const refresh = async () => {
    if (listing) return;
    try {
      await dispatch(fetchUsers({ limit: 50, nextToken: undefined })).unwrap();
      toast.success('Users refreshed');
    } catch (err) {
      const e = err as { message?: string };
      toast.error(e?.message || 'Failed to refresh users');
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold">Admin · Users</h1>
          <p className="text-muted-foreground text-sm">Search application profiles and inspect durable user IDs.</p>
          {listError ? <p className="text-destructive mt-1 text-xs">{listError}</p> : null}
        </div>

        <div className="flex gap-2">
          <Button type="button" variant="outline" onClick={refresh} disabled={listing}>
            {listing ? 'Loading…' : 'Refresh'}
          </Button>
        </div>
      </div>

      <div className="grid gap-6 lg:grid-cols-[420px_1fr]">
        {/* Left: list */}
        <Card>
          <CardHeader>
            <CardTitle>Users</CardTitle>
            <CardDescription>Search by email, name, id, orgId</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <Input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search…" />

            <div className="space-y-2">
              {filtered.length === 0 ? (
                <p className="text-muted-foreground text-sm">{listing ? 'Loading…' : 'No users found.'}</p>
              ) : (
                filtered.map((u) => {
                  const isActive = u.applicationUserId === selectedId;
                  const name = `${u.firstName ?? ''} ${u.lastName ?? ''}`.trim() || u.email;
                  return (
                    <button
                      key={u.applicationUserId}
                      type="button"
                      onClick={() => selectUser(u.applicationUserId)}
                      className={[
                        'w-full rounded-md border p-3 text-left transition',
                        isActive ? 'border-primary bg-primary/5' : 'hover:bg-muted/40',
                      ].join(' ')}
                    >
                      <div className="flex items-center justify-between gap-3">
                        <div className="min-w-0">
                          <p className="truncate text-sm font-medium">{name}</p>
                          <p className="text-muted-foreground truncate text-xs">{u.email}</p>
                        </div>

                        <div className="flex items-center gap-2">
                          {u.allowCreateOrg ? (
                            <Badge>can create org</Badge>
                          ) : (
                            <Badge variant="outline">no org create</Badge>
                          )}
                          {u.organizationID ? (
                            <Badge variant="secondary">in org</Badge>
                          ) : (
                            <Badge variant="outline">no org</Badge>
                          )}
                        </div>
                      </div>
                    </button>
                  );
                })
              )}
            </div>

            {/* Pagination */}
            <div className="pt-2">
              <Button
                type="button"
                variant="outline"
                className="w-full"
                onClick={loadMore}
                disabled={
                  listing || !hasMore || filtered.length !== users.length /* only paginate when not searching */
                }
              >
                {filtered.length !== users.length
                  ? 'Search active (load more disabled)'
                  : listing
                    ? 'Loading…'
                    : hasMore
                      ? 'Load more'
                      : 'No more users'}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Right: editor */}
        <AdminUserDetailsCard
          user={selected}
          canUpdateOrgCreation
          description="Review this profile and manage organization creation access. Other fields are read only."
        />
      </div>
    </div>
  );
}
